import * as Sentry from '@sentry/react';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { AuthenticatedLayout, UnauthenticatedLayout } from './components';
import * as Views from './views';

import ReactGA from 'react-ga4';
import { useSelector } from 'react-redux';
import { useEffect } from 'react';

function App() {
    const location = useLocation();
    const navigate = useNavigate();
    const { user, isLoggedIn } = useSelector((state) => state.auth);

    //Google Analytics is tied in to Google Ads conversion tracking
    ReactGA.initialize(process.env.REACT_APP_GA_TRACKING_ID, {
        testMode: window.location.origin === 'https://local.gem.health:3000',
    });

    const GEMRoutes =
        process.env.REACT_APP_ENVIRONMENT !== 'local'
            ? Sentry.withSentryReactRouterV6Routing(Routes)
            : Routes;

    useEffect(() => {
        //Routes with /dashboard are authenticated routes
        if (!isLoggedIn && location.pathname.includes('/dashboard')) {
            navigate('/sign-in', { state: { from: location.pathname }, replace: true });
            return;
            //If the user is logged in but they are not on a dashboard route, move them to an authenticated route
        } else if (isLoggedIn && user && !location.pathname.includes('/dashboard')) {
            //If they were routed from being not logged in, return them to their origin
            if (location.state?.from) {
                navigate(location.state.from, { replace: true });
                return;
            }

            //Otherwise determine if they have a study to upload or are starting the HST path
            const studyToUpload =
                user?.patientProfile?.stopBangResult?.hasPriorStudy &&
                user?.patientProfile?.stopBangResult?.previouslyDiagnosedWithSleepApnea &&
                !user?.patientProfile?.stopBangResult?.gained20Lbs &&
                user?.patientProfile?.stopBangResult?.howBroughtToGem !== 'NEED_NEW_SLEEP_TEST' &&
                user?.patientProfile?.mdStatus === 'NOT_INITIATED' &&
                user?.patientProfile?.homeSleepStudyStatus === 'NOT_INITIATED' &&
                !user?.files?.some((file) => file.fileType === 'CLINICAL_CHARTS');

            const hstPath =
                !studyToUpload &&
                user?.patientProfile?.mdStatus === 'NOT_INITIATED' &&
                user?.patientProfile?.homeSleepStudyStatus === 'NOT_INITIATED';

            //And route them to the correct place, otherwise homepage
            if (studyToUpload) {
                navigate('/dashboard/diagnosis/study-upload', { replace: true });
            } else if (hstPath) {
                navigate('/dashboard/diagnosis/gem-sleep-study', { replace: true });
            } else {
                navigate('/dashboard', { replace: true });
            }
        }
    }, [location, navigate, user, isLoggedIn]);

    return (
        <GEMRoutes>
            <Route path="/" element={<UnauthenticatedLayout />}>
                <Route index element={<Views.AssessmentHome />} />
                <Route path="partner/:partnerId" element={<Views.AssessmentHome />} />
                <Route
                    path="dental/:dentalPracticeId"
                    element={<Views.AssessmentDentalReferral />}
                />
                <Route path="assessment" element={<Views.Assessment />} />
                <Route path="sign-in" element={<Views.SignIn />} />
                <Route path="reset-password" element={<Views.ResetPassword />} />
                <Route path="account-created" element={<Views.AccountCreationSuccess />} />
                <Route path="mask-comparison" element={<Views.MaskComparison />} />
                <Route path="terms/:termsSlug" element={<Views.Terms theme="dark" />} />
                <Route path="impersonate" element={<Views.Impersonate />} />
            </Route>

            <Route path="/dashboard" element={<AuthenticatedLayout />}>
                <Route index element={<Views.Home />} />
                <Route path="diagnosis" element={<Views.Diagnosis />}>
                    <Route index element={<Views.Diagnosis.Home />} />
                    <Route path="get-diagnosed" element={<Views.Diagnosis.GetDiagnosed />} />
                    <Route
                        path="study-in-review"
                        element={<Views.Diagnosis.SleepStudyInReview />}
                    />
                    <Route
                        path="study-upload"
                        element={<Views.Diagnosis.PatientRecordsUploads />}
                    />
                    <Route path="gem-sleep-study" element={<Views.Diagnosis.GemSleepStudy />} />
                </Route>
                <Route path="resources" element={<Views.Resources />}>
                    <Route index element={<Views.Resources.Home />} />
                    <Route path="blog/:articleSlug" element={<Views.Resources.Article />} />
                </Route>

                <Route path="appointments" element={<Views.Appointments />}>
                    <Route index element={<Views.Appointments.MyAppointment />} />
                    <Route
                        path="reschedule-appointment"
                        element={<Views.Appointments.RescheduleAppointment />}
                    />
                    <Route
                        path="appointment-confirmed"
                        element={<Views.Appointments.AppointmentConfirmed />}
                    />
                    <Route path="my-appointment" element={<Views.Appointments.MyAppointment />} />
                    <Route path="schedule" element={<Views.Appointments.AppointmentOverview />} />
                    <Route
                        path="schedule/:sku"
                        element={<Views.Appointments.AppointmentOverview />}
                    />
                    <Route
                        path="dental-appointment"
                        element={<Views.Appointments.DentalAppointment />}
                    />
                </Route>

                <Route path="treatment" element={<Views.Treatment />}>
                    <Route index element={<Views.Treatment.Home />} />
                    <Route
                        path="dentist-confirmation"
                        element={<Views.Treatment.DentistConfirmation />}
                    />
                    <Route
                        path="treatment-options"
                        element={<Views.Treatment.PreRecommendations />}
                    />
                    <Route path="gem-bundle" element={<Views.Treatment.BundleDescription />} />
                    <Route path="bundle/:bundleId" element={<Views.Treatment.BundleDetails />} />
                    <Route path="comparison" element={<Views.Treatment.ComparisonCharts />} />
                </Route>

                <Route path="checkout" element={<Views.Checkout />}></Route>

                <Route path="insurance" element={<Views.Insurance />}>
                    {/* <Route index element={<Views.Insurance.CurrentInsurance />} /> */}
                    <Route path="add" element={<Views.Insurance.AddInsurance />} />
                    {/* <Route path="update" element={<Views.Insurance.UpdateInsurance />} /> */}
                    {/* <Route path="upload" element={<Views.Insurance.UploadCard />} /> */}
                </Route>

                <Route path="orders" element={<Views.Orders />}>
                    <Route path="estimate/:estimateId" element={<Views.Orders.Estimate />} />
                </Route>

                <Route path="care-team" element={<Views.CareTeam />} />

                <Route path="*" element={<Views.ErrorPage theme="light" />} />
            </Route>

            <Route path="*" element={<Views.ErrorPage theme="dark" />} />
        </GEMRoutes>
    );
}

export default App;
